import PageHeader from '../../../components/PageHeader'
import MainLayout from '../../../layouts/MainLayout'
import Form from '../../../components/forms/Form'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import ItemListSection from '../../../components/forms/ItemListSection'
import useBaseService from '../../../hooks/services/useBaseService'
import useNotification from '../../../hooks/useNotification'
import useLoader from '../../../hooks/useLoader'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import useSelectOptions from '../../../hooks/useSelectOptions'
import useForm from '../../../hooks/useForm'
import { entities } from '../../../constants/entities'
import { cleanPayload, normalizeFormErrors } from '../../../utils/objectUtils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useCallback, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { useOrgan } from '../../../hooks/useOrgan'
import DepartmentEnums from '../../../enums/department-enum'
import { faBox } from '@fortawesome/free-solid-svg-icons'
import createField from '../../../utils/factory/createField'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'

const transactionSection = {
  section: 'Dados da Saída Interna',
  fields: [
    createField({
      id: 'transaction.transaction_date',
      label: 'Data da Saída',
      type: 'date',
      required: true,
    }),
    createField({
      id: 'transaction.department',
      label: 'Departamento',
      type: 'select',
      placeholder: 'Selecione o departamento',
      required: true,
    }),
    createField({
      id: 'transaction.details',
      label: 'Detalhes',
      type: 'textarea',
      placeholder: 'Observações sobre a saída (opcional)',
      fullWidth: true,
    }),
  ],
}

const itemSection = {
  section: 'Insumos',
  array: true,
  fields: [
    createField({
      id: 'items.product_id',
      label: 'Insumo',
      type: 'select',
      placeholder: 'Selecione o insumo',
      icon: faBox,
      required: true,
    }),
    createField({
      id: 'items.quantity',
      label: 'Quantidade',
      type: 'number',
      placeholder: 'Quantidade',
      required: true,
    }),
  ],
}

const CreateInternalExitPage = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { showLoader, hideLoader } = useLoader()
  const { selectedOrgan } = useOrgan()
  const queryClient = useQueryClient()

  const { post: baseServicePost, get: baseServiceGet, formErrors } =
    useBaseService(navigate)

  const { formData, handleChange, setFormData } = useForm({
    transaction: {
      transaction_date: new Date().toISOString().split('T')[0],
      department: '',
      details: '',
    },
  })

  const {
    formData: itemFormData,
    handleChange: handleItemChange,
    setFormData: setItemFormData,
  } = useForm({ items: {} })

  const [items, setItems] = useState([])

  // Select de departamentos (ENUM)
  const departmentOptions = useMemo(
    () =>
      Object.values(DepartmentEnums).map((dept) => ({
        value: dept,
        label: dept,
      })),
    []
  )

  const departmentSelect = useSelectOptions({
    fieldId: 'transaction.department',
    formData,
    setFormData,
    rawOptions: departmentOptions,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  })

  // Select de insumos (só produtos que não são assets)
    // Select de insumos — só produtos COM estoque, com quantidade no label
  const productsSelect = useRemoteSelectOptions({
    queryKey: ['almox_products_internal_exit', selectedOrgan?.id],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      // 1) busca os produtos (insumos)
      const productFilters = transformFiltersForUrl({
        deleted_at: false,
        is_asset: false,
        ...(search && { name: search }),
      })
      const response = await baseServiceGet(entities.products.get, {
        filters: productFilters,
        page,
        perPage,
      })
      const products = response.result?.data ?? []
      if (products.length === 0) return response.result

      // 2) busca o estoque (product_items) com quantidade > 0 desses produtos
      const stockResponse = await baseServiceGet(entities.productsItems.get, {
        filters: transformFiltersForUrl({
          product_id: products.map((p) => p.id),
          quantity: { $gt: 0 },
          ...(selectedOrgan?.id && { organization_id: selectedOrgan.id }),
        }),
        perPage: 200,
      })
      const stockItems = stockResponse.result?.data ?? []

      // 3) soma o estoque por produto
      const stockByProduct = stockItems.reduce((acc, pi) => {
        acc[pi.product_id] = (acc[pi.product_id] ?? 0) + Number(pi.quantity)
        return acc
      }, {})

      // 4) mantém só produtos COM estoque + quantidade entre parênteses
      const data = products
        .filter((p) => (stockByProduct[p.id] ?? 0) > 0)
        .map((p) => ({
          ...p,
          name: `${p.name} (${stockByProduct[p.id]})`,
        }))

      return { ...response.result, data }
    },
    fieldId: 'items.product_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    perPage: 15,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  })

  const itemSelects = {
    'items.product_id': productsSelect,
  }

  const getItemsOptions = useCallback(
    (fieldId) => itemSelects[fieldId]?.options || [],
    [productsSelect]
  )

  const getItemsSelectedValue = useCallback(
    (fieldId) => itemSelects[fieldId]?.selectedValue || null,
    [productsSelect]
  )

  const getItemsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = itemSelects[fieldId]
      if (!selectConfig) return {}
      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [productsSelect]
  )

  // 🆕 Renderiza o corpo do modal de adicionar/editar item
  const renderFormBody = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      )
    },
    []
  )

  // 🆕 Formata a exibição dos valores na tabela (nome do insumo em vez do ID)
  const getItemDisplayValue = useCallback(
    (item, fieldId) => {
      if (fieldId === 'items.product_id') {
        return (
          item.product_label ||
          item.product_id?.label ||
          productsSelect.options?.find((o) => o.value === item.product_id)
            ?.label ||
          item.product_id ||
          ''
        )
      }
      const fieldName = fieldId.split('.').pop()
      const value = item[fieldName]
      if (value && typeof value === 'object' && 'label' in value)
        return value.label
      return value ?? ''
    },
    [productsSelect.options]
  )

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      showLoader()
      const response = await baseServicePost(
        entities.transactions.internalExit,
        payload
      )
      return response
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        queryClient.invalidateQueries({ queryKey: ['almox_internal_exits'] })
        queryClient.invalidateQueries({ queryKey: ['almox_transactions'] })
        showNotification('success', 'Saída interna cadastrada com sucesso!')
        navigate('/almoxarifado/transacoes/saida-interna')
      }
    },
    onError: (err) => {
      console.error('Erro ao criar saída interna:', err)
      showNotification(
        'error',
        err.response?.data?.message || 'Erro ao cadastrar saída interna.'
      )
    },
    onSettled: () => hideLoader(),
  })

  const handleSubmit = useCallback(async () => {
    try {
      if (!formData.transaction?.department) {
        showNotification('warning', 'Selecione o departamento.')
        return
      }
      if (!formData.transaction?.transaction_date) {
        showNotification('warning', 'Informe a data da saída.')
        return
      }
      if (!items || items.length === 0) {
        showNotification('warning', 'Adicione pelo menos um insumo.')
        return
      }

      const cleanedTransaction = cleanPayload(formData.transaction)
      const cleanedItems = items.map((item) => ({
        ...cleanPayload(item),
        product_id: item.product_id?.value ?? item.product_id,
        quantity: Number(item.quantity),
      }))

      const payload = {
        transaction: {
          ...cleanedTransaction,
          transaction_type_id: 3,
          is_internal_exit: true,
          customer_id: null,
          supplier_id: null,
          sender_id: null,
          receiver_id: null,
        },
        items: cleanedItems,
      }

      await createMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Erro:', error)
    }
  }, [formData.transaction, items, createMutation, showNotification])

  const handleSaveItem = useCallback(
    (itemToEdit) => {
      const itemData = itemFormData.items

      // 🆕 aceita {value,label} OU primitivo
      const productId = itemData?.product_id?.value ?? itemData?.product_id

      if (!productId) {
        showNotification('warning', 'Selecione um insumo.')
        return false
      }
      if (!itemData?.quantity || Number(itemData.quantity) <= 0) {
        showNotification('warning', 'Informe uma quantidade válida.')
        return false
      }

      // 🆕 guarda o label pra exibir na tabela
      const selectedOption = productsSelect.options?.find(
        (o) => String(o.value) === String(productId)
      )

      const normalized = {
        ...itemData,
        product_id: productId,
        product_label:
          itemData?.product_id?.label ??
          selectedOption?.label ??
          `#${productId}`,
        quantity: Number(itemData.quantity),
      }

      const updatedItems = itemToEdit
        ? items.map((i) =>
            i.id === itemToEdit.id ? { ...i, ...normalized } : i
          )
        : [...items, { id: crypto.randomUUID(), ...normalized }]

      setItems(updatedItems)
      return true
    },
    [itemFormData, items, productsSelect.options, showNotification]
  )

  const handleItemsChange = useCallback((newItems) => {
    setItems(newItems)
  }, [])

  const handleBack = useCallback(() => {
    navigate('/almoxarifado/transacoes/saida-interna')
  }, [navigate])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastrar Saída Interna"
        showBackButton
        backUrl="/almoxarifado/transacoes/saida-interna"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Cadastrar Saída"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createMutation.isPending}>
          {() => (
            <>
              <div key={transactionSection.section}>
                <SectionHeader section={transactionSection.section} />
                <SimpleBody
                  fields={transactionSection.fields}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={() => departmentOptions}
                  getSelectedValue={(fieldId) =>
                    fieldId === 'transaction.department'
                      ? departmentSelect.selectedValue
                      : null
                  }
                />
              </div>

              <Box
                key={itemSection.section}
                sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                <ItemListSection
                  section={itemSection.section}
                  fields={itemSection.fields}
                  initialData={items}
                  onItemsChange={handleItemsChange}
                  renderFormBody={renderFormBody}
                  getItemDisplayValue={getItemDisplayValue}
                  itemFormData={itemFormData}
                  onItemChange={handleItemChange}
                  itemFormErrors={formErrors}
                  onFormSubmit={handleSaveItem}
                  initializeItemForm={() => setItemFormData({ items: {} })}
                  getOptions={getItemsOptions}
                  getSelectedValue={getItemsSelectedValue}
                  getSelectMeta={getItemsSelectMeta}
                  notShowHeader={['items.details']}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default CreateInternalExitPage